<template>
    <AppForm>
        <FormRow class="modal-body">
            <ComponentLoader v-if="loading"></ComponentLoader>
            <p v-else-if="notFound">{{ $t("DID document not found") }}</p>
            <CodeBlock
                v-else
                :language="'json'"
                :code="didDocument"
                :name="$t('DID Document') + ' (' + (sharedData?.did || did || '') + ')'"
            ></CodeBlock>
        </FormRow>
    </AppForm>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Timeouts } from "@/utils/timeout";
import { Request } from "@asanrom/request-browser";
import { getUniqueStringId } from "@/utils/unique-id";
import ComponentLoader from "@/components/utils/ComponentLoader.vue";
import CodeBlock from "../utils/CodeBlock.vue";
import { ApiDataSpaceParticipants } from "@/api/api-group-data-space-participants";
import { prettifyJson } from "@/utils/objects";
import AppForm from "../forms/form/AppForm.vue";
import FormRow from "../forms/form/FormRow.vue";

export default defineComponent({
    components: {
        ComponentLoader,
        CodeBlock,
        AppForm,
        FormRow,
    },
    name: "DidDocumentModalContent",
    emits: ["update:display"],
    props: {
        display: Boolean,
        did: String,
        sharedData: {
            type: Object,
            default: () => ({}),
        },
    },
    setup() {
        return {
            loadRequestId: getUniqueStringId(),
        };
    },
    data() {
        return {
            loading: true,
            notFound: false,

            didDocument: "",
        };
    },
    methods: {
        load: function () {
            Timeouts.Abort(this.loadRequestId);
            Request.Abort(this.loadRequestId);

            this.loading = true;

            const resolvedDid = this.sharedData?.did || this.did;

            if (!resolvedDid) {
                this.loading = false;
                this.notFound = true;
                return;
            }

            Request.Pending(this.loadRequestId, ApiDataSpaceParticipants.ResolveDidDocument(resolvedDid))
                .onSuccess((res) => {
                    this.loading = false;
                    this.notFound = !res.didDocument;
                    this.didDocument = prettifyJson(JSON.stringify(res.didDocument));
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        badRequest: () => {
                            this.loading = false;
                            this.notFound = true;
                        },
                        temporalError: () => {
                            // Retry
                            Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    // Retry
                    Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                });
        },
    },
    mounted: function () {
        this.load();
        this.$autoFocus();
    },
    beforeUnmount: function () {
        Timeouts.Abort(this.loadRequestId);
        Request.Abort(this.loadRequestId);
    },
    watch: {
        display: function () {
            if (this.display) {
                this.load();
                this.$autoFocus();
            }
        },

        did: function () {
            this.load();
        },

        "sharedData.did": function () {
            this.load();
        },
    },
});
</script>

<style scoped>
.link {
    display: flex;
    align-items: center;
    gap: 6px;

    color: var(--color-text-primary-brand-default);

    font-size: var(--text-sm);
    font-weight: 600;

    text-decoration: none;
}

.modal-dialog {
    padding-bottom: 24px;
    gap: 20px;
}

.modal-body {
    padding-top: 0 !important;
}

/* A DID document can be long, and an expanded code block has no height of its own, so the dialog
   grew past the screen. The cap goes on the <pre>, which already scrolls on its own: capping the
   body instead left two nested scroll areas and cut the background of the highlighted code */
.modal-body :deep(.code-block pre) {
    max-height: 60vh;
}

.modal-title {
    padding-bottom: 0 !important;
    padding-top: 24px;
    gap: var(--space-2);
}
</style>
